import {BRAND_TRAIT_DEFINITIONS, BrandTraitKey} from "data/models/shared/bc-sheet/brand-trait";
import {useObjectVal} from "react-firebase-hooks/database";
import {ref, set} from "firebase/database";
import {database} from "hooks/firebase";
import Loading from "components/Loading";
import React, {useState} from "react";
import {assertNoError} from "helpers/asserts";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import DefinedColors from "./DefinedColors";
import clsx from "clsx";
import ColorPickingComponent from "./ColorPickingComponent";
import {ColorDefinition} from "data/models/shared/recommended-colors3";

type ColorDefinitions = {
  [key in BrandTraitKey]: {
    [key in BrandTraitKey]: ColorDefinition[];
  }
}

type SelectedColor = {
  primaryTrait: BrandTraitKey;
  secondaryTrait: BrandTraitKey;
}

export default function ColorsDefinitionComponent() {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const [colorDefinitions, colorDefinitionsLoading, colorDefinitionsError] =
      useObjectVal<ColorDefinitions>((user && ref(database, "back-office/data-definitions/colors")) ?? null);
  const [selectedColor, setSelectedColor] = useState<SelectedColor | null>(null);

  if (colorDefinitionsLoading || userLoading) {
    return <div className={"flex flex-col"}>
      <Loading className={"mt-24 text-xl"} loadingText={"Loading Colors ..."}/>
    </div>;
  }
  assertNoError(colorDefinitionsError ?? userError)

  function handleSelectColorToChoose(primaryTrait: BrandTraitKey, secondaryTrait: BrandTraitKey) {
    setSelectedColor({primaryTrait, secondaryTrait});
  }

  async function saveNewColors(colors: ColorDefinition[]) {
    if (selectedColor === null) {
      throw new Error("Unexpected condition: selectedColor === null");
    }
    await set(ref(
        database,
        `back-office/data-definitions/colors/${selectedColor.primaryTrait}/${selectedColor.secondaryTrait}`
    ), colors);
  }

  function addDefaultAccentColor(definedColors: ColorDefinition[]): ColorDefinition[] {
    return definedColors.map(c => ({
      ...c,
      accent: c.accent ?? "#00000000",
    }))
  }

  function getTraitColors(primaryTraitKey: BrandTraitKey, secondaryTraitKey: BrandTraitKey) {
    return addDefaultAccentColor(colorDefinitions?.[primaryTraitKey][secondaryTraitKey] ?? []);
  }

  return <div className={"flex flex-row"}>
    <div className={"flex flex-col w-80"}>
      <div className={"h-8"}>&nbsp;</div>
      {
        Object.values(BRAND_TRAIT_DEFINITIONS).map(({title: primaryTitle}, index) => (
                <div key={index}
                     className={"flex flex-col justify-center text-xs pl-4 h-12 my-1 py-1 border border-transparent"}>
                  {primaryTitle}
                </div>
            )
        )}
    </div>
    {
      (Object.keys(BRAND_TRAIT_DEFINITIONS) as BrandTraitKey[]).map((secondaryKey, index) => (
          <div key={index} className={"flex flex-col w-96"}>
            <div
                className={"flex flex-col justify-center text-center text-xs h-8 overflow-clip"}>{BRAND_TRAIT_DEFINITIONS[secondaryKey].title}</div>
            {
              (Object.keys(BRAND_TRAIT_DEFINITIONS) as BrandTraitKey[]).map((primaryKey, index) => <div
                      key={index}
                      className={clsx(
                          "relative h-12 m-1 border",
                          !(selectedColor?.primaryTrait === primaryKey && selectedColor?.secondaryTrait === secondaryKey) ? "border-neutral-50 hover:border-neutral-500" : "border-transparent",
                      )}>
                    {!(selectedColor?.primaryTrait === primaryKey && selectedColor?.secondaryTrait === secondaryKey) && <div
                        className={clsx(
                            "w-full h-full p-1 cursor-pointer",
                        )}
                        onClick={() => handleSelectColorToChoose(primaryKey, secondaryKey)}
                    >

                        <DefinedColors key={index} colors={colorDefinitions?.[primaryKey]?.[secondaryKey] ?? []}/>
                    </div>}
                    {(selectedColor?.primaryTrait === primaryKey && selectedColor?.secondaryTrait === secondaryKey) &&
                        <div className={"absolute z-10"}>
                            <ColorPickingComponent
                                key={index}
                                traits={{
                                  primary: primaryKey,
                                  secondary: secondaryKey,
                                }}
                                definedColors={addDefaultAccentColor(colorDefinitions?.[primaryKey]?.[secondaryKey] ?? [])}
                                onSave={saveNewColors}
                                onClose={() => setSelectedColor(null)}
                                getTraitColors={getTraitColors}
                            />
                        </div>}
                  </div>
              )}
          </div>
      ))
    }
  </div>

}
